import type { Table } from '@tanstack/react-table'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTableFacetedFilter } from '@/components/shared/data-table/data-table-faceted-filter'
import { TaskStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { formatEnumName, getTaskStatusColor } from '@/lib/enum-utils'
import { formatFullName } from '@/lib/utils'
import type { TaskLite } from '@/gen/cortado/v1/actions_pb'

interface TasksTableToolbarProps<TData> {
  table: Table<TData>
  tasks: TaskLite[]
}

export function TasksTableToolbar<TData>({
  table,
  tasks,
}: TasksTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0

  // Get all task status options for the filter
  const taskStatusOptions = [
    TaskStatusEnum.TASK_STATUS_NOT_STARTED,
    TaskStatusEnum.TASK_STATUS_IN_PROGRESS,
    TaskStatusEnum.TASK_STATUS_COMPLETED,
  ].map((status) => ({
    label: formatEnumName(status, TaskStatusEnum),
    value: String(status),
    color: getTaskStatusColor(status),
  }))

  // Get unique assigned users for the filter
  const assignedToOptions = Array.from(
    new Map(
      tasks
        .filter((task) => task.assignedTo)
        .map((task) => {
          const name = formatFullName(
            task.assignedToFirstName,
            task.assignedToLastName,
            'Unassigned'
          )
          return [task.assignedTo, { label: name, value: task.assignedTo }]
        })
    ).values()
  ).map((option) => ({
    label: option.label,
    value: option.value,
  }))

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center space-x-2">
        {table.getColumn('taskStatus') && (
          <DataTableFacetedFilter
            column={table.getColumn('taskStatus')}
            title="Status"
            options={taskStatusOptions}
          />
        )}
        {table.getColumn('assignedTo') && assignedToOptions.length > 0 && (
          <DataTableFacetedFilter
            column={table.getColumn('assignedTo')}
            title="Assigned To"
            options={assignedToOptions}
          />
        )}
        {isFiltered && (
          <Button
            variant="ghost"
            onClick={() => table.resetColumnFilters()}
            className="h-8 px-2 lg:px-3"
          >
            Reset
            <X />
          </Button>
        )}
      </div>
    </div>
  )
}

